import { useDispatch } from 'react-redux';
import { DestructiveButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_VARIANTS, MODAL_SIZES } from 'ui/modal';
import { Body2 } from 'ui/theme';
import { useUpdateOrganizationMembership } from 'api/OrganizationAPI';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { fullName } from '../../utils';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import {
  MemberProjectPayload,
  useOrganizationModalsContext,
  MODAL_NAMES,
} from '../../contexts/OrganizationModalsContext';

export const RemoveFromProjectModal = () => {
  const dispatch = useDispatch();
  const { refetchAll } = useOrganizationMembersQueryContext();
  const { modalState, hideModal } = useOrganizationModalsContext();
  const { member, project } = modalState?.payload as MemberProjectPayload;

  const { mutate, isLoading } = useUpdateOrganizationMembership(member?.userId, {
    onSuccess: () => {
      dispatch(
        addPositiveToast(
          `${fullName(member)} was removed from the ${project.name} project successfully.`,
        ),
      );
      refetchAll();
      hideModal();
    },
    onError: (error: unknown) => {
      const msg = `Error removing ${fullName(member)} from the ${
        project.name
      } project. Please try again.`;
      dispatch(addNegativeToastWithErrorId(msg, 'removeMemberFromProject', error));
      hideModal();
    },
  });

  const onSubmit = () => {
    if (!member || !project) return;

    const projectMemberships = member.projectMemberships.filter(
      (membership) => membership.projectId !== project.id,
    );
    const teamMemberships = member.teamMemberships.filter(
      (membership) => membership.projectId !== project.id,
    );
    const payload = {
      projectMemberships,
      teamMemberships,
    };

    mutate(payload);
  };

  if (modalState?.name !== MODAL_NAMES.REMOVE_FROM_PROJECT || !member || !project) {
    return null;
  }

  return (
    <Modal
      id="remove-from-project-modal"
      isModalOpen
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
      onClose={hideModal}
    >
      <ModalHeader>Remove from project</ModalHeader>
      <ModalBody>
        <Body2>
          Are you sure you want to remove <b>{fullName(member)}</b> from the <b>{project.name}</b>{' '}
          project?
        </Body2>
      </ModalBody>
      <ModalFooter>
        <DestructiveButton
          onClick={onSubmit}
          size={BUTTON_SIZES.LARGE}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Remove from project
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
